// PDF Templates for Content Studio Export
// Uses @react-pdf/renderer for professional PDF generation

import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
} from '@react-pdf/renderer';
import { format } from 'date-fns';

import type {
  FlashcardDeck,
  MindMap,
  MindMapNode,
  Quiz,
  QuizGradingResult,
  StudyGuide,
  SummaryResponse,
  FAQResponse,
  Timeline,
  BriefingDoc,
  PodcastResult,
  DocumentAnalysis,
} from '@/lib/content-studio/types';

// Register fonts (using default fonts for compatibility)
const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 11,
    fontFamily: 'Helvetica',
    backgroundColor: '#ffffff',
  },
  header: {
    marginBottom: 20,
    borderBottom: '2 solid #3b82f6',
    paddingBottom: 15,
  },
  title: {
    fontSize: 24,
    fontFamily: 'Helvetica-Bold',
    color: '#1e293b',
    marginBottom: 5,
  },
  subtitle: {
    fontSize: 12,
    color: '#64748b',
  },
  meta: {
    fontSize: 9,
    color: '#94a3b8',
    marginTop: 8,
  },
  section: {
    marginBottom: 15,
  },
  sectionTitle: {
    fontSize: 14,
    fontFamily: 'Helvetica-Bold',
    color: '#1e40af',
    marginBottom: 8,
    paddingBottom: 4,
    borderBottom: '1 solid #e2e8f0',
  },
  text: {
    fontSize: 10,
    lineHeight: 1.6,
    color: '#334155',
  },
  bulletItem: {
    flexDirection: 'row',
    marginBottom: 4,
    paddingLeft: 10,
  },
  bullet: {
    width: 15,
    fontSize: 10,
    color: '#3b82f6',
  },
  bulletText: {
    flex: 1,
    fontSize: 10,
    color: '#334155',
    lineHeight: 1.5,
  },
  numberedItem: {
    flexDirection: 'row',
    marginBottom: 4,
    paddingLeft: 10,
  },
  number: {
    width: 20,
    fontSize: 10,
    color: '#3b82f6',
    fontFamily: 'Helvetica-Bold',
  },
  keyValue: {
    flexDirection: 'row',
    marginBottom: 6,
    paddingBottom: 6,
    borderBottom: '1 solid #f1f5f9',
  },
  key: {
    width: '35%',
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    color: '#475569',
  },
  value: {
    flex: 1,
    fontSize: 10,
    color: '#334155',
    lineHeight: 1.5,
  },
  table: {
    marginTop: 5,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f1f5f9',
    borderBottom: '1 solid #cbd5e1',
    padding: 6,
  },
  tableHeaderCell: {
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
    color: '#475569',
    flex: 1,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: '1 solid #f1f5f9',
    padding: 6,
  },
  tableCell: {
    fontSize: 9,
    color: '#334155',
    flex: 1,
    paddingRight: 8,
  },
  card: {
    backgroundColor: '#f8fafc',
    borderRadius: 6,
    padding: 16,
    marginBottom: 14,
    borderLeft: '4 solid #3b82f6',
  },
  cardTitle: {
    fontSize: 12,
    fontFamily: 'Helvetica-Bold',
    color: '#1e293b',
    marginBottom: 6,
    lineHeight: 1.5,
  },
  cardContent: {
    fontSize: 11,
    color: '#475569',
    lineHeight: 1.5,
  },
  badge: {
    fontSize: 8,
    backgroundColor: '#dbeafe',
    color: '#1e40af',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    marginRight: 5,
  },
  badgeSuccess: {
    backgroundColor: '#dcfce7',
    color: '#166534',
  },
  badgeError: {
    backgroundColor: '#fee2e2',
    color: '#991b1b',
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 40,
    right: 40,
    textAlign: 'center',
    fontSize: 8,
    color: '#94a3b8',
    borderTop: '1 solid #e2e8f0',
    paddingTop: 10,
  },
  pageNumber: {
    position: 'absolute',
    bottom: 30,
    right: 40,
    fontSize: 9,
    color: '#94a3b8',
  },
  highlight: {
    backgroundColor: '#fef3c7',
    padding: 10,
    borderRadius: 4,
    marginBottom: 10,
  },
  scoreCard: {
    backgroundColor: '#f0fdf4',
    borderRadius: 6,
    padding: 15,
    marginBottom: 15,
    alignItems: 'center',
    borderLeft: '4 solid #22c55e',
  },
  scoreCardFail: {
    backgroundColor: '#fef2f2',
    borderLeft: '4 solid #ef4444',
  },
  scoreText: {
    fontSize: 36,
    fontFamily: 'Helvetica-Bold',
    color: '#16a34a',
  },
  scoreTextFail: {
    color: '#dc2626',
  },
});

// Flashcards PDF
function FlashcardsPDF({ deck }: { deck: FlashcardDeck }) {
  return (
    <>
      <View style={styles.section}>
        <Text style={styles.text}>{deck.description}</Text>
        <Text style={[styles.meta, { marginTop: 5 }]}>
          {deck.totalCards} cards • {deck.categories.length} categories
        </Text>
      </View>

      {deck.cards.map((card, idx) => (
        <View key={card.id} style={styles.card}>
          <View style={{ flexDirection: 'row', marginBottom: 8 }}>
            <Text style={styles.badge}>{card.category || 'General'}</Text>
            <Text style={[styles.badge, 
              card.difficulty === 'easy' ? styles.badgeSuccess : 
              card.difficulty === 'hard' ? styles.badgeError : {}
            ]}>
              {card.difficulty}
            </Text>
          </View>
          <Text style={styles.cardTitle}>Q: {card.front}</Text>
          <Text style={styles.cardContent}>A: {card.back}</Text>
        </View>
      ))}
    </>
  );
}

// Mind Map PDF - Improved visual hierarchy with proper spacing
function MindMapPDF({ mindMap }: { mindMap: MindMap }) {
  // Branch colors for visual distinction
  const branchColors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
  
  const renderSubNode = (node: MindMapNode, level: number, branchColor: string): React.ReactNode => (
    <View key={node.id} style={{ 
      marginLeft: level * 20, 
      marginBottom: 10,
      paddingLeft: 12,
      borderLeftWidth: level > 1 ? 1 : 0,
      borderLeftColor: '#e2e8f0',
    }} wrap={false}>
      <View style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
        <View style={{ 
          width: level === 1 ? 10 : 7, 
          height: level === 1 ? 10 : 7, 
          borderRadius: level === 1 ? 5 : 3.5, 
          backgroundColor: level === 1 ? branchColor : '#94a3b8',
          marginTop: 3,
          marginRight: 10,
          flexShrink: 0,
        }} />
        <View style={{ flex: 1 }}>
          {/* Label on its own line with clear styling */}
          <Text style={{
            fontSize: level === 1 ? 12 : 10,
            lineHeight: 1.6,
            fontFamily: level === 1 ? 'Helvetica-Bold' : 'Helvetica',
            color: level === 1 ? '#1e293b' : '#334155',
            marginBottom: node.description ? 4 : 0,
          }}>
            {node.label}
          </Text>
          {/* Description on a new line with indent and distinct styling */}
          {node.description && (
            <Text style={{ 
              fontSize: 9, 
              color: '#64748b', 
              lineHeight: 1.6,
              paddingLeft: 4,
              marginBottom: 2,
            }}>
              {node.description}
            </Text>
          )}
        </View>
      </View>
      {/* Render children with extra spacing */}
      {node.children && node.children.length > 0 && (
        <View style={{ marginTop: 6 }}>
          {node.children.map(child => renderSubNode(child, level + 1, branchColor))}
        </View>
      )}
    </View>
  );

  return (
    <>
      {/* Central Topic - Big and prominent */}
      <View style={{ 
        backgroundColor: '#1e40af', 
        padding: 20, 
        borderRadius: 8, 
        marginBottom: 24,
        alignItems: 'center',
      }}>
        <Text style={{ fontSize: 20, fontFamily: 'Helvetica-Bold', color: '#ffffff', textAlign: 'center' }}>
          {mindMap.centralTopic}
        </Text>
        <Text style={{ fontSize: 10, color: '#93c5fd', marginTop: 5 }}>
          {mindMap.nodes.length} main branches
        </Text>
      </View>

      {/* Main Branches - allow wrapping across pages */}
      {mindMap.nodes.map((node, idx) => {
        const branchColor = branchColors[idx % branchColors.length];
        return (
          <View key={node.id} style={{ 
            marginBottom: 22, 
            padding: 20, 
            backgroundColor: '#f8fafc',
            borderRadius: 8,
            borderLeftWidth: 5,
            borderLeftColor: branchColor,
          }}>
            {/* Branch header */}
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 14 }} wrap={false}>
              <View style={{ 
                width: 26, 
                height: 26, 
                borderRadius: 13, 
                backgroundColor: branchColor,
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: 12,
                flexShrink: 0,
              }}>
                <Text style={{ color: '#ffffff', fontSize: 12, fontFamily: 'Helvetica-Bold' }}>
                  {idx + 1}
                </Text>
              </View>
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 14, fontFamily: 'Helvetica-Bold', color: '#1e293b' }}>
                  {node.label}
                </Text>
                {node.description && (
                  <Text style={{ fontSize: 10, color: '#64748b', marginTop: 4, lineHeight: 1.5 }}>
                    {node.description}
                  </Text>
                )}
              </View>
            </View>
            
            {/* Children with proper spacing */}
            {node.children && node.children.length > 0 && (
              <View style={{ marginLeft: 38, paddingTop: 10, marginTop: 6, borderTopWidth: 1, borderTopColor: '#e2e8f0' }}>
                {node.children.map(child => renderSubNode(child, 1, branchColor))}
              </View>
            )}
          </View>
        );
      })}

      {/* Connections section if any */}
      {mindMap.connections && mindMap.connections.length > 0 && (
        <View style={{ marginTop: 15, padding: 14, backgroundColor: '#fef3c7', borderRadius: 6 }} wrap={false}>
          <Text style={{ fontSize: 11, fontFamily: 'Helvetica-Bold', color: '#92400e', marginBottom: 10 }}>
            Cross-References
          </Text>
          {mindMap.connections.map((conn, idx) => (
            <Text key={idx} style={{ fontSize: 9, color: '#78350f', marginBottom: 4, lineHeight: 1.5 }}>
              {conn.from}  →  {conn.to}{conn.label ? ` (${conn.label})` : ''}
            </Text>
          ))}
        </View>
      )}
    </>
  );
}

// Quiz PDF
function QuizPDF({ quiz, results }: { quiz: Quiz; results?: QuizGradingResult }) {
  return (
    <>
      {/* Score card if results provided */}
      {results && (
        <View style={[styles.scoreCard, !results.passed && styles.scoreCardFail]}>
          <Text style={[styles.scoreText, !results.passed && styles.scoreTextFail]}>
            {results.percentage}%
          </Text>
          <Text style={[styles.text, { marginTop: 5 }]}>
            {results.correctAnswers} of {results.totalQuestions} correct
          </Text>
          <Text style={[styles.badge, results.passed ? styles.badgeSuccess : styles.badgeError, { marginTop: 8 }]}>
            {results.passed ? 'PASSED' : 'NEEDS IMPROVEMENT'}
          </Text>
        </View>
      )}

      <View style={styles.section}>
        <Text style={styles.text}>{quiz.description}</Text>
        <Text style={styles.meta}>
          {quiz.totalQuestions} questions • Est. {quiz.estimatedTime}
        </Text>
      </View>

      {quiz.questions.map((q, idx) => {
        const result = results?.results?.find(r => r.questionId === q.id);
        return (
          <View key={q.id} style={styles.card}>
            <View style={{ flexDirection: 'row', marginBottom: 8 }}>
              <Text style={styles.badge}>{q.type}</Text>
              <Text style={[styles.badge, 
                q.difficulty === 'easy' ? styles.badgeSuccess : 
                q.difficulty === 'hard' ? styles.badgeError : {}
              ]}>
                {q.difficulty}
              </Text>
              {result && (
                <Text style={[styles.badge, result.isCorrect ? styles.badgeSuccess : styles.badgeError]}>
                  {result.isCorrect ? '✓ Correct' : '✗ Incorrect'}
                </Text>
              )}
            </View>
            
            <Text style={styles.cardTitle}>
              {idx + 1}. {q.question}
            </Text>
            
            {q.scenarioContext && (
              <Text style={[styles.text, { fontStyle: 'italic', marginBottom: 8 }]}>
                Context: {q.scenarioContext}
              </Text>
            )}

            {q.options && (
              <View style={{ marginTop: 8 }}>
                {q.options.map((opt, i) => (
                  <View key={i} style={styles.bulletItem}>
                    <Text style={styles.bullet}>{String.fromCharCode(65 + i)}.</Text>
                    <Text style={styles.bulletText}>{opt}</Text>
                  </View>
                ))}
              </View>
            )}

            {result && (
              <View style={{ marginTop: 10, paddingTop: 8, borderTop: '1 solid #e2e8f0' }}>
                <Text style={styles.text}>
                  Your answer: {String(result.userAnswer)}
                </Text>
                {!result.isCorrect && (
                  <Text style={[styles.text, { color: '#16a34a' }]}>
                    Correct: {String(result.correctAnswer)}
                  </Text>
                )}
              </View>
            )}

            <View style={{ marginTop: 8, padding: 8, backgroundColor: '#fef3c7', borderRadius: 4 }}>
              <Text style={[styles.text, { fontSize: 9 }]}>
                💡 {q.explanation}
              </Text>
            </View>
          </View>
        );
      })}
    </>
  );
}

// Study Guide PDF
function StudyGuidePDF({ guide }: { guide: StudyGuide }) {
  return (
    <>
      <View style={styles.section}>
        <Text style={styles.text}>{guide.overview}</Text>
      </View>

      {guide.learningObjectives?.length > 0 && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Learning Objectives</Text>
          {guide.learningObjectives.map((obj, i) => (
            <View key={i} style={styles.numberedItem}>
              <Text style={styles.number}>{i + 1}.</Text>
              <Text style={styles.bulletText}>{obj}</Text>
            </View>
          ))}
        </View>
      )}

      {guide.sections?.map((section, idx) => (
        <View key={idx} style={styles.section}>
          <Text style={styles.sectionTitle}>{section.title}</Text>
          <Text style={styles.text}>{section.content}</Text>
          {section.keyPoints?.length > 0 && (
            <View style={{ marginTop: 8 }}>
              <Text style={[styles.text, { fontFamily: 'Helvetica-Bold', marginBottom: 4 }]}>
                Key Points:
              </Text>
              {section.keyPoints.map((point, i) => (
                <View key={i} style={styles.bulletItem}>
                  <Text style={styles.bullet}>•</Text>
                  <Text style={styles.bulletText}>{point}</Text>
                </View>
              ))}
            </View>
          )}
        </View>
      ))}

      {guide.glossary?.length > 0 && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Glossary</Text>
          {guide.glossary.map((item, i) => (
            <View key={i} style={styles.keyValue}>
              <Text style={styles.key}>{item.term}</Text>
              <Text style={styles.value}>{item.definition}</Text>
            </View>
          ))}
        </View>
      )}
    </>
  );
}

// Summary PDF
function SummaryPDF({ summary }: { summary: SummaryResponse }) {
  return (
    <>
      <View style={[styles.highlight, { backgroundColor: '#dcfce7' }]}>
        <Text style={[styles.cardTitle, { color: '#166534' }]}>Executive Summary</Text>
        <Text style={styles.text}>{summary.executiveSummary}</Text>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Key Points</Text>
        {summary.keyPoints.map((point, i) => (
          <View key={i} style={styles.bulletItem}>
            <Text style={styles.bullet}>•</Text>
            <Text style={styles.bulletText}>{point}</Text>
          </View>
        ))}
      </View>

      {summary.sections.map((section, idx) => (
        <View key={idx} style={styles.section}>
          <Text style={styles.sectionTitle}>{section.heading}</Text>
          <Text style={styles.text}>{section.content}</Text>
        </View>
      ))}
    </>
  );
}

// FAQ PDF
function FAQPDF({ faq }: { faq: FAQResponse }) {
  return (
    <>
      <View style={styles.section}>
        <Text style={styles.text}>{faq.description}</Text>
      </View>

      {faq.items.map((item, idx) => (
        <View key={item.id} style={styles.card}>
          {item.category && (
            <Text style={[styles.badge, { marginBottom: 8 }]}>{item.category}</Text>
          )}
          <Text style={styles.cardTitle}>Q: {item.question}</Text>
          <Text style={styles.cardContent}>A: {item.answer}</Text>
        </View>
      ))}
    </>
  );
}

// Timeline PDF
function TimelinePDF({ timeline }: { timeline: Timeline }) {
  return (
    <>
      <View style={styles.section}>
        <Text style={styles.text}>{timeline.description}</Text>
      </View>

      {timeline.events.map((event, idx) => (
        <View key={event.id} style={[styles.card, {
          borderLeftColor: event.significance === 'major' ? '#3b82f6' :
            event.significance === 'minor' ? '#94a3b8' : '#e2e8f0'
        }]}>
          <View style={{ flexDirection: 'row', marginBottom: 5 }}>
            <Text style={[styles.badge, { marginRight: 8 }]}>
              {event.date || event.period}
            </Text>
            <Text style={[styles.badge, 
              event.significance === 'major' ? { backgroundColor: '#dbeafe' } : {}
            ]}>
              {event.significance}
            </Text>
          </View>
          <Text style={styles.cardTitle}>{event.title}</Text>
          <Text style={styles.cardContent}>{event.description}</Text>
        </View>
      ))}
    </>
  );
}

// Briefing Doc PDF
function BriefingDocPDF({ doc }: { doc: BriefingDoc }) {
  return (
    <>
      <View style={[styles.highlight, { backgroundColor: '#e0f2fe' }]}>
        <Text style={[styles.cardTitle, { color: '#0369a1' }]}>Executive Summary</Text>
        <Text style={styles.text}>{doc.executiveSummary}</Text>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Background</Text>
        <Text style={styles.text}>{doc.background}</Text>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Key Findings</Text>
        {doc.keyFindings.map((finding, idx) => (
          <View key={idx} style={styles.card}>
            <Text style={[styles.badge, 
              finding.importance === 'critical' ? styles.badgeError :
              finding.importance === 'high' ? { backgroundColor: '#fef3c7', color: '#92400e' } : {}
            ]}>
              {finding.importance.toUpperCase()}
            </Text>
            <Text style={[styles.cardTitle, { marginTop: 5 }]}>{finding.finding}</Text>
            {finding.details && (
              <Text style={styles.cardContent}>{finding.details}</Text>
            )}
          </View>
        ))}
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Recommendations</Text>
        {doc.recommendations.map((rec, i) => (
          <View key={i} style={styles.numberedItem}>
            <Text style={styles.number}>{i + 1}.</Text>
            <Text style={styles.bulletText}>{rec}</Text>
          </View>
        ))}
      </View>

      {doc.nextSteps?.length > 0 && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Next Steps</Text>
          {doc.nextSteps.map((step, i) => (
            <View key={i} style={styles.bulletItem}>
              <Text style={styles.bullet}>→</Text>
              <Text style={styles.bulletText}>{step}</Text>
            </View>
          ))}
        </View>
      )}
    </>
  );
}

// Podcast Transcript PDF
function PodcastTranscriptPDF({ podcast }: { podcast: PodcastResult }) {
  return (
    <>
      <View style={styles.section}>
        <Text style={styles.text}>{podcast.script.description}</Text>
        <Text style={styles.meta}>
          Duration: {podcast.script.totalDuration} • {podcast.script.segments.length} segments
        </Text>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Transcript</Text>
        {podcast.script.segments.map((segment, idx) => (
          <View key={idx} style={{ marginBottom: 12 }}>
            <Text style={[styles.badge, { 
              backgroundColor: segment.speaker === 'host1' ? '#dbeafe' : '#d1fae5',
              color: segment.speaker === 'host1' ? '#1e40af' : '#166534',
              marginBottom: 4
            }]}>
              {segment.speakerName}
            </Text>
            <Text style={styles.text}>{segment.text}</Text>
          </View>
        ))}
      </View>
    </>
  );
}

// Analysis PDF Template
function AnalysisPDF({ analysis }: { analysis: DocumentAnalysis }) {
  return (
    <>
      {/* Summary */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Summary</Text>
        <Text style={styles.text}>{analysis.summary}</Text>
      </View>

      {/* Key Topics */}
      {analysis.keyTopics?.length > 0 && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Key Topics</Text>
          {analysis.keyTopics.map((topic, idx) => (
            <View key={idx} style={[styles.card, { marginBottom: 8 }]}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
                <Text style={{ fontFamily: 'Helvetica-Bold', fontSize: 11 }}>{topic.topic}</Text>
                <Text style={[styles.badge, {
                  backgroundColor: topic.importance === 'high' ? '#fecaca' : 
                    topic.importance === 'medium' ? '#fde68a' : '#e2e8f0',
                  color: topic.importance === 'high' ? '#991b1b' : 
                    topic.importance === 'medium' ? '#92400e' : '#475569',
                }]}>{topic.importance}</Text>
              </View>
              <Text style={styles.text}>{topic.description}</Text>
            </View>
          ))}
        </View>
      )}

      {/* Main Points */}
      {analysis.mainPoints?.length > 0 && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Main Points</Text>
          {analysis.mainPoints.map((point, idx) => (
            <View key={idx} style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>{point}</Text>
            </View>
          ))}
        </View>
      )}

      {/* Terminology */}
      {analysis.terminology?.length > 0 && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Key Terminology</Text>
          {analysis.terminology.map((term, idx) => (
            <View key={idx} style={styles.keyValue}>
              <Text style={styles.key}>{term.term}:</Text>
              <Text style={styles.value}>{term.definition}</Text>
            </View>
          ))}
        </View>
      )}

      {/* Suggested Questions */}
      {analysis.suggestedQuestions?.length > 0 && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Suggested Questions</Text>
          {analysis.suggestedQuestions.map((question, idx) => (
            <View key={idx} style={styles.numberedItem}>
              <Text style={styles.number}>{idx + 1}.</Text>
              <Text style={styles.bulletText}>{question}</Text>
            </View>
          ))}
        </View>
      )}
    </>
  );
}

// Main PDF Document Component
interface ContentPDFDocumentProps {
  type: string;
  data: any;
  results?: QuizGradingResult;
}

export function ContentPDFDocument({ type, data, results }: ContentPDFDocumentProps): React.ReactElement {
  const getTitle = () => {
    if (type === 'podcast-transcript') return data.script?.title || 'Podcast Transcript';
    return data.title || type.charAt(0).toUpperCase() + type.slice(1);
  };

  const getSubtitle = () => {
    switch (type) {
      case 'analyze': return `${data.complexity} level • ${data.estimatedReadTime}`;
      case 'flashcards': return `${data.totalCards} Flashcards`;
      case 'quiz': case 'quiz-results': return data.description;
      case 'summary': return `${data.wordCount} words`;
      default: return data.subtitle || data.description;
    }
  };

  // Use landscape for mind maps since they tend to be wide
  const isLandscape = type === 'mindmap';
  const pageStyle = isLandscape
    ? { ...styles.page, padding: 35 }
    : styles.page;

  return (
    <Document>
      <Page size="A4" orientation={isLandscape ? 'landscape' : 'portrait'} style={pageStyle}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.title}>{getTitle()}</Text>
          {getSubtitle() && <Text style={styles.subtitle}>{getSubtitle()}</Text>}
          <Text style={styles.meta}>
            Generated by MEDD Content Studio • {format(new Date(), 'PPP')}
          </Text>
        </View>

        {/* Content based on type */}
        {type === 'analyze' && <AnalysisPDF analysis={data} />}
        {type === 'flashcards' && <FlashcardsPDF deck={data} />}
        {type === 'mindmap' && <MindMapPDF mindMap={data} />}
        {(type === 'quiz' || type === 'quiz-results') && <QuizPDF quiz={data} results={results} />}
        {type === 'study-guide' && <StudyGuidePDF guide={data} />}
        {type === 'summary' && <SummaryPDF summary={data} />}
        {type === 'faq' && <FAQPDF faq={data} />}
        {type === 'timeline' && <TimelinePDF timeline={data} />}
        {type === 'briefing-doc' && <BriefingDocPDF doc={data} />}
        {type === 'podcast-transcript' && <PodcastTranscriptPDF podcast={data} />}

        {/* Footer */}
        <Text style={styles.footer}>
          Generated by MEDD Content Studio • For educational purposes
        </Text>
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
        />
      </Page>
    </Document>
  );
}

export default ContentPDFDocument;
